import { Component } from '@theme/component';

/*
 * Olympus II page components. Loaded as a module by each op2-* section that
 * needs one; module scripts are fetched and run once per URL, so several
 * sections including the same tag cost nothing extra.
 */

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * A horizontally scrolling rail with previous / next buttons.
 * With the `centered` attribute it also marks the item nearest the middle
 * (`is-center`) and pauses any video that leaves the centre.
 * With `autoplay="<ms>"` it steps forward on its own, wrapping to the start,
 * and holds while hovered, focused, touched or off screen. Never under
 * reduced motion.
 * With `advance-on-end` a centred film that finishes brings the next one to
 * the middle and plays it, wrapping to the first after the last.
 * In a centred rail, a `[data-op2-goto="<n>"]` button anywhere inside brings
 * the nth item to the middle and plays its film.
 *
 * @typedef {{ track: HTMLElement, prev?: HTMLButtonElement, next?: HTMLButtonElement }} RailRefs
 * @extends {Component<RailRefs>}
 */
class Op2Rail extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #abort;
  #frame = 0;
  #timer = 0;
  /** Reasons the autoplay is holding: hover, focus, touch, offscreen, hidden. */
  #holds = new Set();
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {HTMLElement | null} An item whose film should play once it is centred. */
  #pendingPlay = null;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#timer);
  }

  #bind() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const { track } = this.refs;

    track.addEventListener('scroll', () => this.#schedule(), { passive: true, signal });
    window.addEventListener('resize', () => this.#schedule(), { passive: true, signal });

    if (this.hasAttribute('centered')) {
      // Open on the second item so the centred item has a neighbour on each side.
      const items = this.#items();
      const start = items.length > 2 ? items[1] : null;
      if (start) requestAnimationFrame(() => this.#scrollToItem(start, 'instant'));

      track.addEventListener(
        'click',
        (event) => {
          const item = event.target instanceof Element ? event.target.closest('[data-op2-item]') : null;
          if (item instanceof HTMLElement && !item.classList.contains('is-center')) {
            event.preventDefault();
            this.#scrollToItem(item);
          }
        },
        { signal }
      );

      this.addEventListener(
        'click',
        (event) => {
          const button = event.target instanceof Element ? event.target.closest('[data-op2-goto]') : null;
          if (button) this.#playItem(Number(button.getAttribute('data-op2-goto')) - 1);
        },
        { signal }
      );
    }

    if (this.hasAttribute('advance-on-end')) {
      // `ended` does not bubble, so listen in the capture phase.
      track.addEventListener('ended', (event) => this.#handleEnded(event), { capture: true, signal });
    }

    this.#bindAutoplay(signal);
    this.#schedule();
  }

  /** @param {AbortSignal} signal */
  #bindAutoplay(signal) {
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
    this.#holds.clear();

    const delay = Number(this.getAttribute('autoplay'));
    if (!delay || delay < 1000 || reducedMotion() || this.#items().length < 2) return;

    /**
     * @param {string} reason
     * @param {boolean} on
     */
    const hold = (reason, on) => {
      if (on) this.#holds.add(reason);
      else this.#holds.delete(reason);
      this.#queueAutoplay(delay);
    };

    this.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && hold('hover', true), { signal });
    this.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && hold('hover', false), { signal });
    this.addEventListener('focusin', () => hold('focus', true), { signal });
    this.addEventListener('focusout', (e) => {
      if (!(e.relatedTarget instanceof Node && this.contains(e.relatedTarget))) hold('focus', false);
    }, { signal });
    // A swipe holds the rail; it picks up again a full interval after the finger lifts.
    this.refs.track.addEventListener('touchstart', () => hold('touch', true), { passive: true, signal });
    this.refs.track.addEventListener('touchend', () => hold('touch', false), { passive: true, signal });
    document.addEventListener('visibilitychange', () => hold('hidden', document.hidden), { signal });

    this.#holds.add('offscreen');
    this.#observer = new IntersectionObserver(([entry]) => hold('offscreen', !entry?.isIntersecting), {
      threshold: 0.25,
    });
    this.#observer.observe(this);
  }

  /** @param {number} delay */
  #queueAutoplay(delay) {
    clearTimeout(this.#timer);
    if (this.#holds.size > 0) return;
    this.#timer = window.setTimeout(() => {
      this.#advance();
      this.#queueAutoplay(delay);
    }, delay);
  }

  /** Steps one item forward, or back to the start from the end. */
  #advance() {
    const { track } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    if (Math.abs(track.scrollLeft) >= max - 2) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      this.#step(1);
    }
  }

  /**
   * Brings an item to the middle and plays its film once it arrives.
   * @param {number} index
   */
  #playItem(index) {
    const item = this.#items()[index];
    if (!item) return;
    if (item.classList.contains('is-center')) {
      this.#pendingPlay = null;
      item.querySelector('video')?.play().catch(() => {});
      return;
    }
    this.#pendingPlay = item;
    this.#scrollToItem(item);
  }

  /** @param {Event} event */
  #handleEnded(event) {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    const items = this.#items();
    const current = video.closest('[data-op2-item]');
    if (!(current instanceof HTMLElement) || !current.classList.contains('is-center')) return;
    const next = items[(items.indexOf(current) + 1) % items.length];
    if (!next || next === current) return;
    this.#pendingPlay = next;
    this.#scrollToItem(next);
  }

  /** @returns {HTMLElement[]} */
  #items() {
    return /** @type {HTMLElement[]} */ ([...this.refs.track.querySelectorAll('[data-op2-item]')]);
  }

  #isRtl() {
    return getComputedStyle(this.refs.track).direction === 'rtl';
  }

  /**
   * @param {HTMLElement} item
   * @param {ScrollBehavior} [behavior]
   */
  #scrollToItem(item, behavior) {
    const { track } = this.refs;
    const offset = item.offsetLeft + item.offsetWidth / 2 - track.clientWidth / 2;
    track.scrollTo({ left: offset, behavior: behavior ?? (reducedMotion() ? 'instant' : 'smooth') });
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const { track, prev, next } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    if (prev) prev.disabled = position <= 2;
    if (next) next.disabled = position >= max - 2;

    if (!this.hasAttribute('centered')) return;

    const middle = track.getBoundingClientRect().left + track.clientWidth / 2;
    let nearest = null;
    let distance = Infinity;
    for (const item of this.#items()) {
      const box = item.getBoundingClientRect();
      const d = Math.abs(box.left + box.width / 2 - middle);
      if (d < distance) {
        distance = d;
        nearest = item;
      }
    }

    for (const item of this.#items()) {
      const centred = item === nearest;
      item.classList.toggle('is-center', centred);
      if (!centred) {
        for (const video of item.querySelectorAll('video')) {
          if (!video.paused) video.pause();
        }
      }
    }

    // The film queued by the one that just ended starts once it reaches the middle.
    if (nearest && nearest === this.#pendingPlay) {
      this.#pendingPlay = null;
      const video = nearest.querySelector('video');
      if (video) {
        video.currentTime = 0;
        video.play().catch(() => {});
      }
    }
  }

  /** @param {number} direction */
  #step(direction) {
    const { track } = this.refs;
    const [first, second] = this.#items();
    if (!first) return;
    const gap = second ? Math.abs(second.offsetLeft - first.offsetLeft) - first.offsetWidth : 0;
    const distance = (first.offsetWidth + gap) * direction * (this.#isRtl() ? -1 : 1);
    track.scrollBy({ left: distance, behavior: reducedMotion() ? 'instant' : 'smooth' });
  }

  /** Scrolls one item back. */
  handlePrev() {
    this.#pendingPlay = null;
    this.#step(-1);
  }

  /** Scrolls one item forward. */
  handleNext() {
    this.#pendingPlay = null;
    this.#step(1);
  }
}

if (!customElements.get('op2-rail')) customElements.define('op2-rail', Op2Rail);

/**
 * Background film. With data-autoplay it plays muted while it is on (or
 * near) the screen and pauses when scrolled away -- the markup carries
 * preload="none" and no autoplay attribute, so nothing is downloaded until
 * the film is about to be seen. A visitor who prefers reduced motion gets
 * the poster with controls instead.
 *
 * @typedef {{ video?: HTMLVideoElement }} FilmRefs
 * @extends {Component<FilmRefs>}
 */
class Op2Film extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    const { video } = this.refs;
    if (!video) return;

    if (reducedMotion()) {
      video.removeAttribute('autoplay');
      video.pause();
      this.#showControls(video);
      return;
    }

    if (!video.hasAttribute('data-autoplay') && !video.hasAttribute('autoplay')) return;
    video.muted = true;

    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) video.play().catch(() => this.#showControls(video));
        else video.pause();
      },
      { rootMargin: '200px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  /**
   * Hands the film to the visitor: controls on, and back in the accessibility tree.
   * @param {HTMLVideoElement} video
   */
  #showControls(video) {
    video.controls = true;
    video.removeAttribute('aria-hidden');
  }
}

if (!customElements.get('op2-film')) customElements.define('op2-film', Op2Film);
